import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Locale, PortalMeView } from '@lims/shared-types';
import { ERROR_CODES } from '@lims/shared-types';
import { ApiError } from '../api/client.js';
import { changeLanguage } from '../i18n/index.js';
import { portalApi, portalSession, setPortalSessionLostHandler } from './portal-api.js';

/**
 * The portal session — who is signed in to the CLIENT PORTAL, if anyone.
 *
 * Entirely separate from the staff `AuthProvider`: its own storage key, its
 * own query keys (all under `portal`), its own sign-out. The account is read
 * from `GET /portal/me`, which the server re-checks on every request — a
 * deactivation or the laboratory switching the portal off takes effect on the
 * very next call, and this context only reports it.
 */

export const PORTAL_QUERY_KEY = ['portal'] as const;

interface PortalSessionValue {
  /** The signed-in account, once `/portal/me` has answered. */
  readonly me: PortalMeView | null;
  readonly isLoading: boolean;
  /** The laboratory has the portal switched off (403 PORTAL_NOT_AVAILABLE). */
  readonly isUnavailable: boolean;
  readonly error: unknown;
  readonly signIn: (email: string, password: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
  /** Persists a language choice to the account (limitation 78) — the switch itself already applies it locally. */
  readonly updatePreferredLocale: (locale: Locale) => Promise<void>;
}

const PortalSessionContext = createContext<PortalSessionValue | null>(null);

export function PortalSessionProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [hasSession, setHasSession] = useState(() => portalSession.read() !== null);

  useEffect(() => {
    setPortalSessionLostHandler(() => {
      setHasSession(false);
      queryClient.removeQueries({ queryKey: PORTAL_QUERY_KEY });
      navigate('/portal/login', { replace: true });
    });
    return () => setPortalSessionLostHandler(null);
  }, [navigate, queryClient]);

  const me = useQuery({
    queryKey: [...PORTAL_QUERY_KEY, 'me'],
    queryFn: portalApi.me,
    enabled: hasSession,
    retry: false,
    staleTime: 60 * 1000,
  });

  const signIn = useCallback(
    async (email: string, password: string) => {
      const account = await portalApi.login(email, password);
      queryClient.setQueryData([...PORTAL_QUERY_KEY, 'me'], account);
      setHasSession(true);
      await changeLanguage(account.preferredLocale);
    },
    [queryClient],
  );

  const updatePreferredLocale = useCallback(
    async (locale: Locale) => {
      const account = await portalApi.updateMe(locale);
      queryClient.setQueryData([...PORTAL_QUERY_KEY, 'me'], account);
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      await portalApi.logout();
    } finally {
      setHasSession(false);
      queryClient.removeQueries({ queryKey: PORTAL_QUERY_KEY });
      navigate('/portal/login', { replace: true });
    }
  }, [navigate, queryClient]);

  const isUnavailable =
    me.error instanceof ApiError && me.error.code === ERROR_CODES.PORTAL_NOT_AVAILABLE;

  const value = useMemo<PortalSessionValue>(
    () => ({
      me: hasSession ? (me.data ?? null) : null,
      isLoading: hasSession && me.isLoading,
      isUnavailable,
      error: me.error,
      signIn,
      signOut,
      updatePreferredLocale,
    }),
    [hasSession, me.data, me.isLoading, me.error, isUnavailable, signIn, signOut, updatePreferredLocale],
  );

  return <PortalSessionContext.Provider value={value}>{children}</PortalSessionContext.Provider>;
}

export function usePortalSession(): PortalSessionValue {
  const value = useContext(PortalSessionContext);
  if (!value) throw new Error('usePortalSession must be used inside PortalSessionProvider');
  return value;
}
